import * as B from '@blueprintjs/core';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {ApiFeature} from 'app/modules/Remote/Feature/types';
import {useUserInfo} from 'app/providers/AuthProvider';
import {formatDuration} from 'app/utils/dateUtils';
import {alphanumericSort, formatFeatureName} from 'app/utils/featureUtils';
import * as routeUtils from 'app/utils/routeUtils';
import * as stringUtils from 'app/utils/stringUtils';
import {isUpstreamEmail} from 'app/utils/userUtils';

import {DataConfigDisplay, DataConfigDisplayToggle} from './DataConfigDisplay';
import {
  useAgentWorkflowRunsPaginatedQuery,
  useArchiveAgentWorkflowRun,
  useStartAgentWorkflowRun,
} from './hooks';
import {RunProgressBar, RunProgressBarStatus} from './RunProgressBar';
import cs from './styles.module.scss';
import {ApiAgentWorkflow} from './types';

type Props = {
  workflow: ApiAgentWorkflow;
  features: ApiFeature[];
};

const DELETE_RUN_MESSAGE =
  "Delete this run? Observations from it that haven't been saved will be dismissed.";
const CANCEL_RUN_MESSAGE =
  'This run is still going. Deleting it stops the work in progress and dismisses observations ' +
  "that haven't been saved. Continue?";

export const AgentWorkflow: React.FC<Props> = ({workflow, features}) => {
  const workflowFeatures = features.filter((f) => workflow.featureIds.includes(f.id));
  const [organization, profile] = useUserInfo();
  const isUpstreamInternalOrgUser = isUpstreamEmail(profile.get('email'));
  const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));
  const navigate = useNavigate();
  const {runs, isLoading, hasNextPage, loadNextPage, reset} = useAgentWorkflowRunsPaginatedQuery(
    workflow.id
  );
  const archiveRun = useArchiveAgentWorkflowRun(workflow.id);

  const [showAllFeatures, setShowAllFeatures] = React.useState(false);
  const [showPromptDetails, setShowPromptDetails] = React.useState(false);

  const [hoveredRunId, setHoveredRunId] = React.useState<number | null>(null);

  return (
    <div className={cs.page}>
      <div className={cs.pageContent}>
        <B.Breadcrumbs
          items={[
            {
              text: 'Run plans',
              onClick: () => {
                navigate(`/${orgIdPrefix}/agent`);
              },
            },
            {
              text: workflow.title,
              onClick: () => {
                navigate(`/${orgIdPrefix}/agent/workflows/${workflow.id}`);
              },
            },
          ]}
        />

        <B.Card>
          <h3 style={{marginTop: '0px', letterSpacing: 'none'}}>
            Run plan details{' '}
            <DataConfigDisplayToggle
              showPromptDetails={showPromptDetails}
              setShowPromptDetails={setShowPromptDetails}
            />
          </h3>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
            <div>
              <span className={cs.label}>Frequency</span>
              {stringUtils.capitalizeFirstLetter(workflow?.frequency) || 'None'}
            </div>
            <div>
              <span className={cs.label}>Description</span>
              {workflow.description}
            </div>
            <div>
              <span className={cs.label}>Next run</span>
              Sept 1, 2025
            </div>
            <div>
              <div className={cs.label} style={{marginTop: '1rem', marginBottom: '0.5rem'}}>
                Properties ({workflowFeatures.length})
              </div>
              <div className={cs.featureTags}>
                {workflowFeatures
                  .map((feature) => formatFeatureName(feature))
                  .sort(alphanumericSort)
                  .map((formattedFeatureName, index) => (
                    <B.Tag key={index} minimal={true} large={true}>
                      {formattedFeatureName}
                    </B.Tag>
                  ))
                  .slice(0, showAllFeatures ? undefined : 10)}
                {workflowFeatures.length > 10 && (
                  <B.Button
                    text={
                      showAllFeatures ? 'Show less' : `Show ${workflowFeatures.length - 10} more`
                    }
                    onClick={() => setShowAllFeatures(!showAllFeatures)}
                    intent="primary"
                    small
                    minimal
                  />
                )}
              </div>
            </div>
            {showPromptDetails && <DataConfigDisplay dataConfig={workflow.dataConfig} />}
          </div>
        </B.Card>

        {isUpstreamInternalOrgUser && <RunWorkflowCard workflow={workflow} />}

        {/** inital load (won't show up when hitting Load More*/}
        {isLoading && !runs && <B.Spinner />}

        {runs && runs.length > 0 && (
          <B.Section title="Runs">
            <B.CardList bordered={false}>
              {runs
                .sort((a, b) => (b.startedAt > a.startedAt ? 1 : -1))
                .map((run) => {
                  const start = new Date(run.startedAt);
                  const end = run.endedAt ? new Date(run.endedAt) : null;

                  const runId = run.id;

                  //this shouldn't happen, but handle bad data
                  if (!run || !runId) {
                    return null;
                  }

                  const isArchiving = archiveRun.isPending && archiveRun.variables === runId;

                  return (
                    <B.Card
                      key={runId}
                      style={{display: 'flex', justifyContent: 'space-between'}}
                      interactive={true}
                      onClick={() => {
                        if (isArchiving) {
                          return;
                        }
                        navigate(`/${orgIdPrefix}/agent/workflows/${workflow.id}/runs/${runId}`);
                      }}
                      onMouseEnter={() => setHoveredRunId(runId)}
                      onMouseLeave={() => setHoveredRunId(null)}
                    >
                      <div style={{display: 'flex', gap: '2rem'}}>
                        <div>
                          {run.status === 'completed' && (
                            <div>
                              <B.Icon
                                className={cs.icon}
                                icon="tick-circle"
                                intent="success"
                                size={20}
                              />
                            </div>
                          )}
                          {run.status === 'failed' && (
                            <div>
                              <B.Icon
                                className={cs.icon}
                                icon="cross-circle"
                                intent="danger"
                                size={20}
                              />
                            </div>
                          )}
                          {['in_progress', 'pending'].includes(run.status) && (
                            <div style={{display: 'flex', alignItems: 'center'}}>
                              <B.Spinner className={cs.icon} size={20} />
                            </div>
                          )}
                        </div>
                        <div className={cs.runTimeContainer}>
                          <div className={cs.runTime}>
                            <B.Icon icon="calendar" />{' '}
                            {start.toLocaleString(undefined, {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </div>
                          {end && (
                            <div className={cs.runTime}>
                              <B.Icon icon="time" /> {formatDuration(start, end)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{minWidth: '20rem'}}>
                          {hoveredRunId === runId ? (
                            <RunProgressBarStatus
                              savedCount={run.savedCount}
                              dismissedCount={run.archivedCount}
                              newCount={run.newCount}
                              showLabels={false}
                            />
                          ) : (
                            <RunProgressBar
                              savedCount={run.savedCount}
                              dismissedCount={run.archivedCount}
                              newCount={run.newCount}
                            />
                          )}
                        </div>
                        <B.Icon icon="chevron-right" />
                        {isUpstreamInternalOrgUser && (
                          <div className={cs.runDeleteSlot}>
                            {hoveredRunId === runId && (
                              <B.Tooltip content="Delete run">
                                <B.Button
                                  icon="trash"
                                  minimal
                                  small
                                  disabled={isArchiving}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const isUnfinished = ['pending', 'in_progress'].includes(
                                      run.status
                                    );
                                    if (
                                      window.confirm(
                                        isUnfinished ? CANCEL_RUN_MESSAGE : DELETE_RUN_MESSAGE
                                      )
                                    ) {
                                      archiveRun.mutate(runId, {onSuccess: reset});
                                    }
                                  }}
                                />
                              </B.Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    </B.Card>
                  );
                })}
            </B.CardList>

            {hasNextPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                <B.Button
                  text="Load more runs"
                  intent="primary"
                  fill={true}
                  minimal={true}
                  loading={isLoading && runs.length > 0}
                  onClick={loadNextPage}
                />
              </div>
            )}
          </B.Section>
        )}

        {/** blankstate */}
        {!isLoading && runs && runs.length === 0 && (
          <B.Card>
            <B.NonIdealState
              icon="blank"
              iconMuted={false}
              title="No runs yet"
              description="Reach out to your Lens team to get started."
            />
          </B.Card>
        )}
      </div>
    </div>
  );
};

const RunWorkflowCard: React.FC<{
  workflow: ApiAgentWorkflow;
}> = ({workflow}) => {
  const {mutateAsync, isPending, isError} = useStartAgentWorkflowRun(workflow.id, workflow.title);

  const startRun = async () => {
    try {
      await mutateAsync({prompt: workflow.dataConfig.prompt});
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <B.Card>
      <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
        <div style={{display: 'flex', gap: '0.5rem'}}>
          <B.Button
            text={'Run plan'}
            intent="primary"
            icon="play"
            onClick={startRun}
            loading={isPending}
            disabled={isPending}
            className={cs.agentAction}
          />
        </div>
        {isError && (
          <B.Callout intent="danger" icon="error">
            Failed to start the run. Please try again.
          </B.Callout>
        )}
      </div>
    </B.Card>
  );
};
